<template>
    <div class="episode-end-screen">
        <div v-if="hasBtnReplay" class="episode-end-screen__wrap-img">
            <img 
                v-if="image" 
                class="episode-end-screen__img" 
                :class="{'episode-end-screen__img_blured': isParentallyRestricted}"
                :src="image" alt="image"
            >
            <common-icon v-else class="episode-end-screen__img" :icon-name="'IconNoPicture'" />
        </div>
        <div 
            class="episode-end-screen__buttons"
            tabindex="0"
            @focus="onFocusBtnsContainer"
        >
            <button 
                v-show="props.hasBtnReplay" 
                :id="buttonsIds.btnReplay" 
                ref="btnReplayEl" 
                class="episode-end-screen__replay"
                :data-keyright="buttonsIds.btnNextEpisode" 
                @click="onClickReplay"
                @blur="onBlurBtnReplay()"
                @focus="onFocusBtnReplay()"
            >
                <player-icon class="episode-end-screen__replay_icon" :icon-name="'IconRestart'" />
                <span class="episode-end-screen__next-episode_index">{{ labels.btnReplay }}</span>
            </button>
            <button 
                v-show="props.hasBtnNextEpisode" 
                :id="buttonsIds.btnNextEpisode" 
                ref="btnNextEpisodeEl" 
                class="episode-end-screen__next-episode" 
                :class="{'episode-end-screen__next-episode_autoplay': isEnableAutoplay}" 
                :style="{'--progress': progress+'%' }"
                :data-keyleft="buttonsIds.btnReplay"
                @click="onClickNextEpisode"
                @blur="onBlurBtnNextEpisode()"
                @focus="onFocusBtnNextEpisode()"
            >
                <player-icon class="episode-end-screen__next-episode_icon" :icon-name="'IconPlaySingle'" />
                <span class="episode-end-screen__next-episode_index">{{ labels.btnNextEpisode }} </span>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { onMounted, computed, type Ref, ref, type PropType } from "vue";
    import { storeToRefs } from 'pinia';
    import { useUserSettingsStore } from "@/stores/UserSettings";
    import {Strings} from "@/locales/types/enums/strings";
    import { useI18n } from "vue-i18n";
    import { type ExposeInterface, ButtonsIds, type ILabels } from "./types";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import {AppConstants} from "@/common";
    import type { MediaImage } from "@ca.vmedia.tv/domains";

    const { needsUnlockPin } = useParentControl()
    const { t } = useI18n();
    const { $navigator, $domains } = useNuxtApp();
    const emit = defineEmits(['closePlayer', 'onClickReplay', 'onClickNextEpisode']);

    const UserSettingsStoreModule = useUserSettingsStore();
    const {
        getEnabledAutoplay: isEnableAutoplay
    } = storeToRefs(UserSettingsStoreModule);
   
    const props = defineProps({
        hasBtnReplay: {
            type: Boolean,
            default: true
        },
        hasBtnNextEpisode: {
            type: Boolean,
            default: true
        },
        playerTime: {
            type: Number,
            required: true
        },
        startimeForEpisodeEndScreen: {
            type: Number,
            required: true
        },
        advisory: {
            type: [Number, null] as PropType<number | null>,
            required: true
        },
        images: {
			type: [Array, null] as PropType<MediaImage[] | null>,
        	required: true
      	}
    });
    
    const isParentallyRestricted = computed((): boolean => needsUnlockPin(props.advisory));
    const progress = computed((): number => {
       const time =  isEnableAutoplay.value ? (props.playerTime - props.startimeForEpisodeEndScreen) * 100 / AppConstants.durationForEpisodeEndScreen : 0;

        return time > 90 ? 100 : time;
    });

    const btnReplayEl: Ref<HTMLButtonElement | null> = ref(null);
    const btnNextEpisodeEl: Ref<HTMLButtonElement | null> = ref(null);
    const buttonsIds = {
        btnReplay: ButtonsIds.btnReplay,
        btnNextEpisode: ButtonsIds.btnNextEpisode
    };
    const labels: ILabels = {
        btnReplay: t(Strings.PLAYER_REPLAY).toString(),
        btnNextEpisode: t(Strings.PLAYER_SKIP_NEXT).toString(),
    };

    const onClickReplay = () => {
        emit("onClickReplay");
    };
    const onClickNextEpisode = () => {
        emit("onClickNextEpisode");
    };

    const closePlayer = (): void => {
        emit("closePlayer");
    };
    const onFocusBtnReplay = (): void => {
        $navigator.on("keypress:back", closePlayer);
    };
    const onBlurBtnReplay = (): void => {
        $navigator.off("keypress:back", closePlayer);
    };
    const onFocusBtnNextEpisode = (): void => {
        $navigator.on("keypress:back", closePlayer);
    };
    const onBlurBtnNextEpisode = (): void => {
        $navigator.off("keypress:back", closePlayer);
    };
    const focusToBtnNextEpisode = (): void => {
        if(btnNextEpisodeEl.value) {
            btnNextEpisodeEl.value.focus();
        }
    };
    const focusToBtnReplay = (): void => {
        if(btnReplayEl.value) {
            btnReplayEl.value.focus();
        }
    };
    const focusDefault = (): void => {
        if(props.hasBtnReplay) {
            focusToBtnReplay();
        } else {
            focusToBtnNextEpisode();
        }
    };
    const onFocusBtnsContainer = (): void => {
        focusDefault();
    };

    const image: Ref<string | null> = ref(null);
    const fetchBackgroundImage = async (): Promise<void> => {
        try {
            image.value = await $domains.imagesRepository.getOnDemandBackgroundImage(props.images);
            
            if (!image.value) {
                image.value = await $domains.imagesRepository.getOnDemandEpisodeImage(props.images);
            }

            if (!image.value) {
                image.value = await $domains.imagesRepository.getOnDemandImage(props.images);
            }

            if (!image.value) {
                image.value = await $domains.imagesRepository.getProgramImage(props.images);
            }
        } catch (error) {
            image.value = null;
        }
    };
    onMounted((): void => {
        fetchBackgroundImage();
        focusDefault();
    });

    defineExpose<ExposeInterface>({
        focusToBtnNextEpisode,
        focusToBtnReplay
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>